import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  FileInput,
  Textarea,
  Alert,
  Loader,
  Code,
  Image,
  CopyButton,
  ActionIcon,
  Tooltip,
  Checkbox,
  NumberInput,
  Divider,
  Modal,
  TextInput,
  Select,
  Progress,
  Switch
} from '@mantine/core';
import {
  IconArrowLeft,
  IconUpload,
  IconAlertCircle,
  IconDownload,
  IconCopy,
  IconCheck,
  IconPhoneCall,
  IconPlayerPlayFilled,
  IconExternalLink,
  IconPlus,
  IconTrash
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { importDiagrams, judgeImport } from '../api_scripts/importsClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import { listAllowlist } from '../api_scripts/allowlistClient';
import { useDids } from '../hooks/useDids';
import { runBatchSequentially, reorderForPreconditionGrouping } from '../utils/batchRunner';
import { createBatch } from '../api_scripts/batchesClient';
import { RuntimePreconditionGateModal, hasPreconditions } from './PreconditionsBadge';
import SynthesizedPlanList from './SynthesizedPlanList';
import JudgeProgressModal from '../../../components/JudgeProgressModal';

// PDFs accepted alongside images so a multi-page Lucid / draw.io / WxCC
// export can be dropped whole. Bedrock Sonnet walks every page in a single
// document block; no client-side splitting.
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
// fromDid default is now env-driven via useDids() — the SPA fetches the list
// from /dids and seeds the Select with the primary DID. Hardcoded fallback
// kept as a last-resort placeholder for when the backend hasn't been deployed
// with the dids Lambda yet; in any healthy env the hook supplies the value.
const FALLBACK_FROM_DID_PLACEHOLDER = '+10000000000';
const DEFAULT_MAX_PLANS = 8;
const MAX_DIAGRAMS = 10;

const IVR_PACE_PRESETS = { fast: 5, standard: 8, slow: 15 };
const IVR_PACE_OPTIONS = [
  { value: 'fast', label: 'Fast — touch-tone menus (5s)' },
  { value: 'standard', label: 'Standard — modern IVRs (8s)' },
  { value: 'slow', label: 'Slow — government / healthcare / long preambles (15s)' },
  { value: 'custom', label: 'Custom — set my own…' }
];

function resolveBaseTimeout(pace, custom) {
  if (pace === 'custom') {
    const n = Number(custom);
    if (!Number.isFinite(n)) return IVR_PACE_PRESETS.fast;
    // Chime SDK SMA Pause action rejects > 30000ms; align the frontend bound.
    return Math.min(30, Math.max(5, Math.round(n)));
  }
  return IVR_PACE_PRESETS[pace] ?? IVR_PACE_PRESETS.fast;
}

function newDiagramRow(isRoot) {
  return {
    // Local row id; backend receives this as diagram.id so parent/child wiring
    // is stable as the user reorders or deletes rows.
    id: `d${Math.random().toString(36).slice(2, 8)}`,
    label: isRoot ? 'Main menu' : '',
    file: null,
    previewUrl: null,
    parentDiagramId: null,
    // Generic default so multi-upload "just works" — the importer prompt only
    // uses parentBranch as context, never as a strict identifier. User can
    // override with something specific ("press 1", "after agent transfer", ...)
    // when the diagram exposes a clear branch label.
    parentBranch: isRoot ? '' : 'linked sub-flow'
  };
}

// Best-effort guess at how the caller reaches this child diagram from the
// parent, based on the file name. Substring match (no \b — author tools like
// WxCC Flow Designer name files as CamelCase like "HelpDeskQueueChecks" with
// no word boundaries inside). Caller can always overwrite — this just removes
// the empty-required-field friction on multi-upload.
function inferParentBranchFromFilename(filename) {
  if (!filename) return 'linked sub-flow';
  const lower = filename.toLowerCase();
  // Strip extension so "voicemail.png" → "voicemail" before hint matching.
  const stem = lower.replace(/\.[^.]+$/, '');
  if (/sub[-_ ]?flow/.test(stem)) return 'subflow call';
  if (/queue|hold|onhold/.test(stem)) return 'queue / hold treatment';
  if (/transfer|handoff|hand-off/.test(stem)) return 'transfer to next flow';
  if (/voicemail|leave[-_ ]?message/.test(stem)) return 'voicemail path';
  if (/survey|feedback/.test(stem)) return 'post-call survey';
  if (/error|failure|fault|fallback/.test(stem)) return 'error / fallback path';
  if (/closed|after[-_ ]?hours|holiday/.test(stem)) return 'closed / after-hours path';
  if (/agent|operator/.test(stem)) return 'agent / operator path';
  return 'linked sub-flow';
}

function DiagramImporter({ onBack }) {
  const [rows, setRows] = useState([newDiagramRow(true)]);
  const [notes, setNotes] = useState('');
  const [maxPlans, setMaxPlans] = useState(DEFAULT_MAX_PLANS);
  const [ivrStyle, setIvrStyle] = useState('hybrid');
  // IVR pace preset → default expectPrompt timeoutSec the importer uses on
  // every step. Custom opens a NumberInput for the longest expected prompt
  // duration. Backend clamps to [5, 60].
  const [ivrPace, setIvrPace] = useState('fast');
  const [customTimeoutSec, setCustomTimeoutSec] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedIndexes, setSelectedIndexes] = useState(new Set());

  // Quality-judge advisory state. judgeRunIdRef guards against stale
  // responses when the user regenerates before the previous judge finished.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);

  // Batch run modal state
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchDest, setBatchDest] = useState(null);
  const { dids, primary: primaryDid, loading: didsLoading } = useDids();
  const [batchFromDid, setBatchFromDid] = useState('');

  // Once /dids returns, lock the batch fromDid to the env's primary if the
  // engineer hasn't already picked one. This handles the common case where
  // the modal mounts before /dids resolves.
  useEffect(() => {
    if (!batchFromDid && primaryDid) setBatchFromDid(primaryDid);
  }, [primaryDid, batchFromDid]);
  const [allowlist, setAllowlist] = useState([]);
  const [batchProgress, setBatchProgress] = useState(null);
  // "Watch in this tab" toggle. When OFF (default), the batch is
  // enqueued to SQS so the backend batch-runner drives dispatch — the
  // tab can close freely and progress lives on the Batches page. When
  // ON, falls back to in-browser dispatch so this tab shows live run
  // output. The toggle is forced ON (and disabled) when any selected
  // plan has preconditions, because those runtime gates need the SPA
  // alive to confirm each IVR flip.
  const [watchInTab, setWatchInTab] = useState(false);

  const backendConfigured = isSitBackendConfigured();
  const navigate = useNavigate();
  const resultsRef = useRef(null);

  useEffect(() => {
    if (result?.plans?.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const loadAllowlist = useCallback(async () => {
    if (!backendConfigured) return;
    try {
      const entries = await listAllowlist();
      setAllowlist(entries.map((e) => ({ value: e.did, label: `${e.did} — ${e.owner}` })));
    } catch { /* non-fatal */ }
  }, [backendConfigured]);

  useEffect(() => { loadAllowlist(); }, [loadAllowlist]);

  // Clean up object URLs when rows change/unmount.
  useEffect(() => {
    return () => rows.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleFileChange = (idx, file) => {
    const prev = rows[idx];
    if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
    updateRow(idx, { file, previewUrl: file ? URL.createObjectURL(file) : null });
    setResult(null);
    setError(null);
    setSelectedIndexes(new Set());
  };

  const addDiagram = () => {
    setRows((prev) => {
      if (prev.length >= MAX_DIAGRAMS) return prev;
      const root = prev[0];
      // Default parent for a fresh child row is the root (most common case).
      const child = newDiagramRow(false);
      child.parentDiagramId = root?.id ?? null;
      return [...prev, child];
    });
  };

  // Bulk-upload: take an array of File objects and distribute them across rows.
  // First file goes into the first EMPTY existing row (typically the Main menu
  // row when the user is starting fresh). Subsequent files become new child
  // rows parented to the root. Stops at MAX_DIAGRAMS — extras are silently
  // dropped with a single error toast so the user knows.
  const handleMultiUpload = (filesIn) => {
    const files = Array.isArray(filesIn) ? filesIn.filter(Boolean) : [];
    if (files.length === 0) return;
    setError(null);
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      let cursor = 0;
      let dropped = 0;
      for (const file of files) {
        // Find next slot — either an existing empty row or a fresh child.
        while (cursor < next.length && next[cursor].file) cursor++;
        if (cursor >= next.length) {
          if (next.length >= MAX_DIAGRAMS) {
            dropped++;
            continue;
          }
          const root = next[0];
          const child = newDiagramRow(false);
          child.parentDiagramId = root?.id ?? null;
          next.push(child);
        }
        const target = next[cursor];
        if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
        target.file = file;
        target.previewUrl = URL.createObjectURL(file);
        // Don't overwrite a label the user already typed (e.g. "Main menu").
        if (!target.label) target.label = file.name.replace(/\.[^.]+$/, '').slice(0, 60);
        // For non-root rows, refine the default parentBranch from the filename
        // (e.g. "HelpDesk_SubflowGetQueueInfo.png" → "subflow call"). Only
        // overwrite the generic default — don't clobber a value the user typed.
        if (cursor > 0 && (target.parentBranch === '' || target.parentBranch === 'linked sub-flow')) {
          target.parentBranch = inferParentBranchFromFilename(file.name);
        }
        cursor++;
      }
      if (dropped > 0) {
        setError(`${dropped} file${dropped > 1 ? 's' : ''} skipped — max ${MAX_DIAGRAMS} diagrams per import.`);
      }
      return next;
    });
  };

  const removeDiagram = (idx) => {
    if (idx === 0) return; // root is required
    setRows((prev) => {
      const removing = prev[idx];
      if (removing?.previewUrl) URL.revokeObjectURL(removing.previewUrl);
      // Reparent any rows that were pointing at the removed one.
      return prev
        .filter((_, i) => i !== idx)
        .map((r) => (r.parentDiagramId === removing.id ? { ...r, parentDiagramId: prev[0]?.id ?? null } : r));
    });
  };

  const handleSubmit = async () => {
    const filled = rows.filter((r) => r.file);
    if (filled.length === 0) return;
    if (rows.some((r) => !r.file)) {
      setError('Every diagram row needs an image file. Remove the empty row or upload an image.');
      return;
    }
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      if (!r.parentDiagramId) {
        setError(`Diagram "${r.label || `row ${i + 1}`}" needs a parent diagram.`);
        return;
      }
      if (!r.parentBranch.trim()) {
        setError(`Diagram "${r.label || `row ${i + 1}`}" needs a parent branch (e.g. "press 1").`);
        return;
      }
    }
    return runImport();
  };

  // Build a text description of the diagram tree for the judge. The judge
  // doesn't see the image bytes — it grades structural concerns (terminality,
  // matcher-type fit, brand-hallucination) against this textual descriptor.
  const buildDiagramSourceContent = (diagramRows, notesText) => {
    const lines = ['DIAGRAM TREE (image bytes NOT re-sent to judge; labels + parent hints only):'];
    diagramRows.forEach((r, i) => {
      const parent = r.parentDiagramId
        ? diagramRows.find((p) => p.id === r.parentDiagramId)?.label || r.parentDiagramId
        : '(root)';
      lines.push(
        `  [${i + 1}] label="${r.label || `Diagram ${i + 1}`}", parent="${parent}", branch="${r.parentBranch || '(n/a)'}"`
      );
    });
    if (notesText) lines.push(`\nENGINEER NOTES:\n${notesText}`);
    return lines.join('\n');
  };

  const startJudge = async ({ plans, diagramRows, notesText }) => {
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const sourceContent = buildDiagramSourceContent(diagramRows, notesText);
      const data = await judgeImport({
        kind: 'sit-plan',
        source: {
          kind: 'diagram',
          description: `${diagramRows.length} diagram(s); judge has labels + notes only, NOT image bytes`
        },
        sourceContent,
        plans
      });
      if (judgeRunIdRef.current !== runId) return;
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  async function runImport(priorJudgeFindings) {
    setSubmitting(true);
    setError(null);
    setResult(null);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const diagramRows = rows.map((r, i) => ({
        id: r.id,
        label: r.label || (i === 0 ? 'Main menu' : `Diagram ${i + 1}`),
        file: r.file,
        parentDiagramId: i === 0 ? null : r.parentDiagramId,
        parentBranch: i === 0 ? null : r.parentBranch
      }));
      const trimmedNotes = notes.trim();
      const payload = {
        diagrams: diagramRows,
        notes: trimmedNotes || undefined,
        maxPlans,
        ivrStyle,
        expectPromptBaseTimeoutSec: resolveBaseTimeout(ivrPace, customTimeoutSec),
        ...(priorJudgeFindings && priorJudgeFindings.length > 0
          ? { priorJudgeFindings }
          : {})
      };
      const data = await importDiagrams(payload);
      setResult(data);
      setSelectedIndexes(new Set((data.plans || []).map((_, i) => i)));
      if (data.plans?.length > 0) {
        startJudge({ plans: data.plans, diagramRows, notesText: trimmedNotes });
      }
    } catch (e) {
      const body = e?.response?.data;
      // Prefer `context.detail` — that's where the importer puts Bedrock's
      // actual prose ("image too small, re-export at higher resolution"),
      // which is the only string that tells the user what to do next. The
      // top-level `message` is the SitError summary and is often generic.
      setError(
        body?.context?.detail ||
          body?.message ||
          body?.code ||
          e.message ||
          'Import failed.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  const handleRegenerate = async (findings) => runImport(findings);

  const toggleIndex = (i) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const setAll = (on) => {
    if (!result?.plans) return;
    setSelectedIndexes(on ? new Set(result.plans.map((_, i) => i)) : new Set());
  };

  const selected = useMemo(() => {
    const list = [];
    (result?.plans || []).forEach((p, i) => { if (selectedIndexes.has(i)) list.push({ plan: p, index: i }); });
    return list;
  }, [result, selectedIndexes]);

  // Runtime gate state — opened mid-batch by runBatchSequentially whenever
  // the upcoming plan needs different IVR setup than the previous one.
  const [gate, setGate] = useState(null); // { plan, position, resolve }
  const openGate = (plan, position) =>
    new Promise((resolve) => setGate({ plan, position, resolve }));
  const resolveGate = (decision) => {
    const r = gate?.resolve;
    setGate(null);
    r?.(decision);
  };

  const handleBatchRun = async () => {
    if (selected.length === 0 || !batchDest) return;
    return fireBatchRun(selected);
  };

  const fireBatchRun = async (selectedSnapshot) => {
    const diagrams = result?.diagrams ?? [];
    // Group plans by precondition signature so the engineer flips each set
    // of IVR switches once and the runner doesn't pause between adjacent
    // plans that share the same setup.
    const ordered = reorderForPreconditionGrouping(selectedSnapshot.map((s) => s.plan));
    const anyPreconditions = ordered.some((p) => hasPreconditions(p));
    // Dispatch mode decision:
    //   - any plan has preconditions → inbrowser (needs user to flip IVR
    //     switches between groups; the runtime gate can't run server-side)
    //   - no preconditions + "Watch in this tab" OFF → server (SQS →
    //     batch-runner, survives tab close; progress on Batches page)
    //   - no preconditions + "Watch in this tab" ON  → inbrowser (user
    //     wants live progress here; accepts tab-close stops dispatch)
    const useServerDispatch = !anyPreconditions && !watchInTab;
    if (useServerDispatch) {
      // Server-dispatched: batches Lambda enqueues to SQS, batch-runner
      // consumes and drives each test run. Browser can close freely —
      // Batches tab reflects progress from DynamoDB counters.
      try {
        const batch = await createBatch({
          plans: ordered,
          overrides: { destination: batchDest, fromDid: batchFromDid },
          label: `${ordered.length} plans — diagram import`
        });
        setBatchOpen(false);
        navigate(`/history/batches/${batch.batchId}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('createBatch (server) failed:', err);
      }
      return;
    }
    // In-browser dispatch (needed when any plan has preconditions). The
    // batch row is created server-side with mode=inbrowser so the SPA's
    // runBatchSequentially can POST progress updates and the Batches tab
    // sees the same row shape as server-driven batches.
    let inBrowserBatchId = null;
    try {
      const inBrowserBatch = await createBatch({
        plans: ordered,
        overrides: { destination: batchDest, fromDid: batchFromDid },
        label: `${ordered.length} plans — diagram import`,
        mode: 'inbrowser'
      });
      inBrowserBatchId = inBrowserBatch.batchId;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('createBatch (inbrowser) failed; runs will still execute but no batch row:', err);
    }
    // After reordering we lose the original selection-row index, so derive
    // the display name from each plan directly.
    await runBatchSequentially({
      plans: ordered,
      overrides: { destination: batchDest, fromDid: batchFromDid },
      diagrams,
      onProgress: setBatchProgress,
      onPreconditionChange: (plan, position) => openGate(plan, position),
      ...(inBrowserBatchId ? { batchId: inBrowserBatchId } : {}),
      planName: (plan, i) => plan?.metadata?.name || plan?.metadata?.id || `Plan ${i + 1}`
    });
  };

  // For each row > 0, the parent dropdown excludes that row and its
  // descendants (to prevent cycles). For v1 we only block exact-self.
  const parentOptionsFor = (idx) =>
    rows.filter((_, i) => i !== idx && rows[i]?.id).map((r) => ({ value: r.id, label: r.label || r.id }));

  return (
    <Container size="lg" py="lg">
      <Group mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
      </Group>

      <Stack gap="md">
        <Group gap="xs" align="center">
          <Title order={2}>Diagram Importer</Title>
        </Group>

        <Text c="dimmed">
          Upload one IVR diagram, or upload several that form a tree (main menu + sub-flow per branch).
          For each non-root diagram, tell the model which parent diagram it continues from and how the
          caller gets there (e.g. "press 1"). Claude on Bedrock walks every root-to-leaf path and drafts
          one test plan per caller path.
        </Text>

        {!backendConfigured && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Backend not configured">
            Set <Code>REACT_APP_SIT_API_BASE</Code> in your <Code>.env</Code> and restart the dev server.
          </Alert>
        )}

        <Card withBorder padding="lg" radius="md">
          <Stack gap="md">
            {rows.map((row, idx) => (
              <Card key={row.id} withBorder padding="sm" radius="sm">
                <Stack gap="xs">
                  <Group justify="space-between" align="center">
                    <Group gap="xs">
                      <Badge variant="filled" color={idx === 0 ? 'violet' : 'gray'} size="sm">
                        {idx === 0 ? 'Root' : `Child ${idx}`}
                      </Badge>
                      <TextInput
                        size="xs"
                        placeholder={idx === 0 ? 'Main menu' : 'e.g. Billing queue'}
                        value={row.label}
                        onChange={(e) => updateRow(idx, { label: e.currentTarget.value })}
                        disabled={submitting}
                        style={{ width: 240 }}
                      />
                    </Group>
                    {idx > 0 && (
                      <Tooltip label="Remove this diagram">
                        <ActionIcon variant="subtle" color="red" onClick={() => removeDiagram(idx)} disabled={submitting}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>

                  <Group grow align="flex-start">
                    <FileInput
                      placeholder="Click to select an image"
                      accept={ACCEPTED_TYPES}
                      value={row.file}
                      onChange={(f) => handleFileChange(idx, f)}
                      leftSection={<IconUpload size={14} />}
                      disabled={submitting}
                      size="sm"
                    />
                    {idx > 0 && (
                      <>
                        <Select
                          placeholder="Parent diagram"
                          data={parentOptionsFor(idx)}
                          value={row.parentDiagramId}
                          onChange={(v) => updateRow(idx, { parentDiagramId: v })}
                          disabled={submitting}
                          size="sm"
                        />
                        <TextInput
                          placeholder="Entry branch (e.g. press 1)"
                          value={row.parentBranch}
                          onChange={(e) => updateRow(idx, { parentBranch: e.currentTarget.value })}
                          disabled={submitting}
                          size="sm"
                        />
                      </>
                    )}
                  </Group>

                  {row.previewUrl && row.file?.type === 'application/pdf' && (
                    // <embed> is the broadest-supported way to preview a PDF
                    // — Chrome/Firefox/Safari all use their bundled viewer.
                    // Fall back to a name + size line so the user still sees
                    // confirmation when the embed is blocked.
                    <Stack gap={4}>
                      <embed
                        src={row.previewUrl}
                        type="application/pdf"
                        style={{ width: '100%', height: 280, border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}
                        title={`${row.label} PDF preview`}
                      />
                      <Text size="xs" c="dimmed">
                        {row.file.name} · {(row.file.size / 1024).toFixed(0)} KB
                      </Text>
                    </Stack>
                  )}
                  {row.previewUrl && row.file?.type?.startsWith('image/') && (
                    <Image src={row.previewUrl} alt={`${row.label} preview`} mah={200} fit="contain" />
                  )}
                </Stack>
              </Card>
            ))}

            <Group justify="space-between">
              <Group gap="sm">
                <Button
                  variant="subtle"
                  leftSection={<IconPlus size={14} />}
                  onClick={addDiagram}
                  disabled={submitting || rows.length >= MAX_DIAGRAMS}
                >
                  Add diagram ({rows.length}/{MAX_DIAGRAMS})
                </Button>
                <FileInput
                  placeholder="Or upload multiple images at once"
                  accept={ACCEPTED_TYPES}
                  multiple
                  clearable
                  value={[]}
                  onChange={handleMultiUpload}
                  leftSection={<IconUpload size={14} />}
                  disabled={submitting || rows.length >= MAX_DIAGRAMS}
                  size="sm"
                  style={{ width: 320 }}
                />
              </Group>
              <Group gap="md">
                <Select
                  label="Destination IVR style"
                  description="Tells the model what kinds of steps to emit"
                  data={[
                    { value: 'hybrid', label: 'Hybrid (default — read the diagram literally)' },
                    { value: 'dtmf-menu', label: 'DTMF menu only (no voice input)' },
                    { value: 'voice-ai', label: 'Voice-AI only (no DTMF menu)' }
                  ]}
                  value={ivrStyle}
                  onChange={(v) => setIvrStyle(v || 'hybrid')}
                  size="sm"
                  style={{ width: 320 }}
                  disabled={submitting}
                />
                <Select
                  label="IVR pace"
                  description="Default expectPrompt timeoutSec"
                  data={IVR_PACE_OPTIONS}
                  value={ivrPace}
                  onChange={(v) => setIvrPace(v || 'fast')}
                  size="sm"
                  style={{ width: 320 }}
                  disabled={submitting}
                />
                {ivrPace === 'custom' && (
                  <NumberInput
                    label="Custom default timeout (sec)"
                    description="Duration of your LONGEST expected IVR prompt (max 30s — Chime SDK Pause limit). You can still override individual steps on the Verify Plans screen."
                    value={customTimeoutSec}
                    onChange={(v) => setCustomTimeoutSec(typeof v === 'number' ? v : 15)}
                    min={5}
                    max={30}
                    size="sm"
                    style={{ width: 220 }}
                    disabled={submitting}
                  />
                )}
                <NumberInput
                  label="Max plans"
                  value={maxPlans}
                  onChange={(v) => setMaxPlans(typeof v === 'number' ? v : DEFAULT_MAX_PLANS)}
                  min={1}
                  max={25}
                  size="sm"
                  style={{ width: 120 }}
                  disabled={submitting}
                />
              </Group>
            </Group>

            <Textarea
              label="Notes for the model (optional)"
              description="Language, queue naming conventions, vendor quirks."
              placeholder="e.g. UK English, billing transfers to Genesys queue SUPPORT_TIER1"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              minRows={2}
              autosize
              disabled={submitting}
            />

            <Group justify="flex-end">
              <Button
                color="violet"
                onClick={handleSubmit}
                disabled={!rows[0]?.file || submitting || !backendConfigured}
                leftSection={submitting ? <Loader size="xs" color="white" /> : null}
              >
                {submitting ? 'Generating plans…' : `Generate plans from ${rows.length} diagram${rows.length > 1 ? 's' : ''}`}
              </Button>
            </Group>
          </Stack>
        </Card>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Import failed">{error}</Alert>
        )}

        {result?.warnings?.length > 0 && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Some plans dropped">
            <Stack gap={2}>
              {result.warnings.map((w, i) => <Text size="xs" key={i}>{w}</Text>)}
            </Stack>
          </Alert>
        )}

        {result?.plans?.length > 0 && (
          <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
            <SynthesizedPlanList
              plans={result.plans}
              modelId={result.modelId}
              retries={result.retries}
              warnings={result.warnings || []}
              sourceLabel="diagram import"
              selectable
              selectedIndexes={selectedIndexes}
              onToggleSelect={toggleIndex}
              onVerify={(plans) =>
                navigate('/verify', { state: { plans, source: 'diagram import' } })
              }
              evaluating={judgeEvaluating}
              qualityScore={qualityScore}
              qualityFindings={qualityFindings}
              onRegenerate={handleRegenerate}
              extraHeaderActions={
                <>
                  <Button size="xs" variant="subtle" onClick={() => setAll(true)}>Select all</Button>
                  <Button size="xs" variant="subtle" onClick={() => setAll(false)}>Clear</Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="violet"
                    leftSection={<IconPlayerPlayFilled size={14} />}
                    disabled={selected.length === 0 || !backendConfigured}
                    onClick={() => setBatchOpen(true)}
                  >
                    Run {selected.length}
                  </Button>
                </>
              }
            />
          </div>
        )}
      </Stack>

      <Modal opened={batchOpen} onClose={() => !batchProgress && setBatchOpen(false)} title="Batch run" size="lg">
        <Stack gap="md">
          {!batchProgress ? (
            <>
              <Text size="sm" c="dimmed">
                Each selected plan starts an independent test run with the destination and fromDid below.
                Runs execute one at a time — the next call doesn't dial until the previous one completes
                — so multiple plans against a single destination phone don't collide on the wire.
                Expect ~1-3 minutes per call.
              </Text>
              {selected.some((s) => hasPreconditions(s.plan)) && (
                <Alert color="orange" variant="light" title="Some plans need IVR setup">
                  Plans that need switches flipped are auto-grouped so each setup happens once.
                  The batch will pause before each new group — flip the switches on the IVR, then click Resume.
                  Keep this tab open — the runtime gates need an active SPA session.
                </Alert>
              )}
              <Switch
                label="Watch progress in this tab"
                description={
                  selected.some((s) => hasPreconditions(s.plan))
                    ? 'Forced on — plans with preconditions need in-tab runtime gates.'
                    : watchInTab
                      ? 'Dispatches each run from this tab. Closing the tab stops the batch.'
                      : 'Default: batch runs in the background on the SIT backend. Close the tab anytime; follow progress on the Batches page.'
                }
                checked={watchInTab || selected.some((s) => hasPreconditions(s.plan))}
                onChange={(e) => setWatchInTab(e.currentTarget.checked)}
                disabled={selected.some((s) => hasPreconditions(s.plan))}
              />
              <Select
                label="Destination (allowlist)"
                placeholder="Pick an allowlisted DID"
                data={allowlist}
                value={batchDest}
                onChange={setBatchDest}
                searchable
                description={allowlist.length === 0 ? 'Allowlist is empty — add a DID under "Destination Allowlist".' : null}
              />
              <Select
                label="From DID (harness)"
                placeholder={didsLoading ? 'Loading…' : (dids.length === 0 ? 'No DIDs configured in this env' : 'Pick a Chime DID')}
                data={dids.map((d) => ({ value: d.did, label: d.primary ? `${d.did} (primary)` : d.did }))}
                value={batchFromDid || (didsLoading ? '' : FALLBACK_FROM_DID_PLACEHOLDER)}
                onChange={(v) => setBatchFromDid(v || '')}
                searchable
                disabled={didsLoading || dids.length === 0}
                description={dids.length === 0 && !didsLoading ? 'GET /dids returned an empty list. Check the chime-sma module\'s harness_dids tfvars.' : null}
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setBatchOpen(false)}>Cancel</Button>
                <Button
                  color="violet"
                  leftSection={<IconPhoneCall size={14} />}
                  disabled={!batchDest || selected.length === 0}
                  onClick={handleBatchRun}
                >
                  Start {selected.length} runs
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Progress value={(batchProgress.done / batchProgress.total) * 100} animated={batchProgress.done < batchProgress.total} />
              <Text size="sm">{batchProgress.done} / {batchProgress.total} runs completed</Text>
              {batchProgress.current && (
                <Group gap="xs">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">
                    <strong>{batchProgress.current.name}</strong> — {batchProgress.current.status}
                  </Text>
                </Group>
              )}
              {batchProgress.results.length > 0 && (
                <Stack gap={4}>
                  <Text fw={600} size="sm">Started</Text>
                  {batchProgress.results.map((r) => (
                    <Group key={r.testRunId} gap="xs">
                      <Badge color="green" variant="light" size="xs">{r.name}</Badge>
                      <Code>{r.testRunId}</Code>
                      <Button size="xs" variant="subtle" onClick={() => navigate(`/history/${r.testRunId}`)}>
                        report
                      </Button>
                    </Group>
                  ))}
                </Stack>
              )}
              {batchProgress.errors.length > 0 && (
                <Stack gap={4}>
                  <Text fw={600} size="sm" c="red">Failed</Text>
                  {batchProgress.errors.map((e) => (
                    <Group key={e.index} gap="xs">
                      <Badge color="red" variant="light" size="xs">{e.name}</Badge>
                      <Text size="xs">{e.error}</Text>
                    </Group>
                  ))}
                </Stack>
              )}
              {batchProgress.done === batchProgress.total && (
                <Group justify="flex-end">
                  <Button variant="subtle" onClick={() => { setBatchOpen(false); setBatchProgress(null); }}>
                    Close
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Modal>

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via evaluating flag */ }}
        kind="sit-plan"
        color="violet"
      />

      <RuntimePreconditionGateModal
        opened={!!gate}
        plan={gate?.plan}
        position={gate?.position}
        onResume={() => resolveGate({ proceed: true })}
        onSkip={() => resolveGate({ skip: true })}
        onCancel={() => resolveGate({ cancel: true })}
      />
    </Container>
  );
}

export default DiagramImporter;
